import { useEffect, useState } from 'react';
import { cn } from '@/utils/cn';
import type { ConversationMessageContent } from '../../types/conversation-message';

/**
 * Parses the memory block's `text` (the distilled summary) into a short excerpt
 * for the card — fail-soft: if nothing parses, the caller shows only the title +
 * a neutral label, never a raw dump of the full summary.
 *
 * Mirrors the native `MemorySummaryReport` rules: the first plain line is the
 * title (owned by the card header), `## ` lines are chapters (skipped for the
 * excerpt), `- ` lines are facts, and a line whose every token is a `#hashtag`
 * (not a `##` heading) is skipped until tags have a real interaction.
 */
function parseMemoryBody(text: string): { excerpt: string } {
  let excerpt = '';
  let sawTitle = false;
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    const tokens = line.split(/\s+/);
    const isTagLine =
      tokens.length > 0 &&
      tokens.every(
        t => t.startsWith('#') && t.length > 1 && !t.startsWith('##')
      );
    if (isTagLine) {
      continue;
    }
    if (line.startsWith('#')) continue; // chapter heading — not the excerpt
    const isBullet =
      line.startsWith('- ') || line.startsWith('* ') || line.startsWith('•');
    if (isBullet) {
      if (!excerpt) excerpt = line.replace(/^[-*•]\s*/, '').trim();
      continue;
    }
    if (!sawTitle) {
      // First plain line is the title (rendered in the header) — skip it.
      sawTitle = true;
      continue;
    }
    if (!excerpt) excerpt = line;
  }
  return { excerpt };
}

/**
 * Renders a sent Memory/App Shot attachment as a CARD in the transcript — title,
 * redacted thumbnail, and a 1–2 line excerpt — never the raw summary Markdown as
 * a user text bubble. Only the bounded summary in the block's `text` is
 * model-facing; everything shown here is display-only.
 */
export function MemoryAttachmentCard({
  content,
}: {
  content: ConversationMessageContent;
}) {
  const title = (content.title ?? '').trim() || 'Memory';
  const source = (content.description ?? '').trim();
  const { excerpt } = parseMemoryBody(content.text ?? '');
  const subtitle = excerpt || 'Memory context';
  const memoryId = (content.memoryId ?? '').trim();
  const directThumbnail = (content.url ?? '').trim();
  const [resolvedThumbnail, setResolvedThumbnail] = useState<string | null>(
    null
  );
  const thumbnail = directThumbnail || resolvedThumbnail;
  const openLabel =
    memoryId.length > 0 ? `Open Memory: ${title}` : 'Open Memories';

  useEffect(() => {
    let cancelled = false;
    setResolvedThumbnail(null);

    if (directThumbnail || !memoryId) {
      return () => {
        cancelled = true;
      };
    }

    const loadThumbnail = window.jsb?.MessagesBridge?.getMemoryThumbnailDataURL;
    if (!loadThumbnail) {
      return () => {
        cancelled = true;
      };
    }

    void loadThumbnail(memoryId)
      .then(url => {
        if (!cancelled && url) {
          setResolvedThumbnail(url);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [directThumbnail, memoryId]);

  const openMemory = () => {
    void window.jsb?.MessagesBridge?.openMemory?.(memoryId);
  };

  return (
    <div
      className={cn(
        'memory-attachment-card',
        'flex max-w-[min(100%,21rem)] items-start gap-3 rounded-2xl p-2.5',
        'border border-black/10 bg-black/[0.04]',
        'dark:border-white/10 dark:bg-white/[0.04]',
        'cursor-pointer transition-colors hover:bg-black/[0.06] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-500 dark:hover:bg-white/[0.06]'
      )}
      data-memory-id={memoryId || undefined}
      role="button"
      tabIndex={0}
      aria-label={openLabel}
      onClick={openMemory}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openMemory();
        }
      }}
    >
      {thumbnail ? (
        <img
          src={thumbnail}
          alt=""
          className="size-12 shrink-0 rounded-xl object-cover"
        />
      ) : (
        <div className="size-12 shrink-0 rounded-xl bg-gradient-to-br from-violet-400/40 to-indigo-500/40" />
      )}
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex items-center gap-1.5">
          <span className="rounded-full bg-violet-500/15 px-1.5 py-0.5 text-[0.625rem] font-semibold uppercase tracking-wide text-violet-600 dark:text-violet-300">
            App Shot
          </span>
          {source && (
            <span className="truncate text-xs text-muted-foreground">
              {source}
            </span>
          )}
        </div>
        <div className="truncate text-sm font-medium">{title}</div>
        <div className="line-clamp-2 text-xs text-muted-foreground">
          {subtitle}
        </div>
      </div>
    </div>
  );
}
